import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import type { WizardData } from './types'

interface StepBasicsProps {
  data: WizardData
  onChange: (data: WizardData) => void
  errors: Record<string, string>
}

export function StepBasics({ data, onChange, errors }: StepBasicsProps) {
  const set = (patch: Partial<WizardData>) => onChange({ ...data, ...patch })

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold">Core Settings</h3>
        <p className="text-sm text-muted-foreground">
          Set up the basic configuration for your SRO instance.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="config-name">
          Configuration Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id="config-name"
          placeholder="e.g. City of Doral Service Requests"
          value={data.configName}
          onChange={(e) => set({ configName: e.target.value })}
          className={errors.configName ? 'border-destructive' : ''}
        />
        {errors.configName && (
          <p className="text-xs text-destructive">{errors.configName}</p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>AI Provider</Label>
          <Select value={data.provider} onValueChange={(v) => set({ provider: v })}>
            <SelectTrigger>
              <SelectValue placeholder="Select provider" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="openai">OpenAI</SelectItem>
              <SelectItem value="anthropic">Anthropic</SelectItem>
              <SelectItem value="google">Google Gemini</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="model">Model</Label>
          <Input
            id="model"
            value={data.model}
            onChange={(e) => set({ model: e.target.value })}
            placeholder="gpt-4o-mini"
          />
          <p className="text-xs text-muted-foreground">
            {data.provider === 'anthropic'
              ? 'e.g. claude-3-5-haiku-latest, claude-sonnet-4-20250514'
              : data.provider === 'google'
                ? 'e.g. gemini-2.0-flash, gemini-1.5-pro'
                : 'e.g. gpt-4o-mini, gpt-4o'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="session-ttl">Session TTL (seconds)</Label>
          <Input
            id="session-ttl"
            type="number"
            value={data.sessionTTL}
            onChange={(e) => set({ sessionTTL: parseInt(e.target.value) || 0 })}
          />
          <p className="text-xs text-muted-foreground">
            How long a conversation session stays active
          </p>
        </div>

        <div className="space-y-4 pt-2">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="enable-sro">Enable SRO</Label>
              <p className="text-xs text-muted-foreground">
                Activate service request orchestration
              </p>
            </div>
            <Switch
              id="enable-sro"
              checked={data.enableSRO}
              onCheckedChange={(v) => set({ enableSRO: v })}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="skip-address">Skip Address Validation</Label>
              <p className="text-xs text-muted-foreground">
                Treat location as free-text instead of street address
              </p>
            </div>
            <Switch
              id="skip-address"
              checked={data.serviceArea.skip_address_validation}
              onCheckedChange={(v) =>
                onChange({
                  ...data,
                  serviceArea: { ...data.serviceArea, skip_address_validation: v },
                })
              }
            />
          </div>
        </div>
      </div>
    </div>
  )
}
